import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableChartConfigDto, UmpirePitchCallChartDataDto, UmpirePitchCallDataDto } from 'types/CodegenReportTypes';
import Table, { Order } from 'components/Table/Table';
import { ChartProps } from 'utilities/chartIdToComponent';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import PitchLocationTableCell from 'components/Table/PitchLocationTableCell';
import PitchTypeTableCell from 'components/Table/PitchTypeTableCell';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';

export type UmpirePitchCallChartColumn = keyof UmpirePitchCallDataDto;

const UmpirePitchCallChart = ({ chartData, isLoading, chartConfig }: ChartProps<UmpirePitchCallChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<UmpirePitchCallChartColumn | '' | 'id'>('');

  const { selectedPitchNumber, setSelectedPitchNumber } = useChartBrokerStore();

  const rows = useMemo(
    () =>
      chartData?.umpirePitchCalls.map((row, i) => ({
        ...row,
        id: row.pitchOfGame?.toString() ?? i.toString(),
      })) ?? [],
    [chartData?.umpirePitchCalls]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<UmpirePitchCallChartColumn>(chartConfig.visibleStats);

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: t(column),
      headerShortName: t('short.' + column),
      sortable: true,
      renderCell: (cellValue: any, row: UmpirePitchCallDataDto) => {
        switch (column) {
          case 'inning':
            return (
              <span>
                {cellValue}
                <span style={{ opacity: 0.3 }}>{row.halfInning === 'Top' ? ' ▲' : ' ▼'}</span>
              </span>
            );
          case 'pitcherHandedness':
          case 'batterHandedness':
          case 'umpireCall':
            return t(`short.${cellValue}`);
          case 'callCorrectness':
            return t(cellValue);
          case 'pitchLocation':
            return <PitchLocationTableCell pitchLocation={cellValue} pitchType={row.pitchType} playerType='Batter' />;
          case 'pitchType':
            return <PitchTypeTableCell pitchType={cellValue} />;
          default:
            return cellValue;
        }
      },
    }));
  }, [chartConfig.visibleStats]);

  const isRowSelected = useCallback((row: UmpirePitchCallDataDto) => selectedPitchNumber === row.pitchOfGame, [selectedPitchNumber]);

  const handleRowSelect = useCallback(
    (row: UmpirePitchCallDataDto) => {
      selectedPitchNumber === row.pitchOfGame ? setSelectedPitchNumber(undefined) : setSelectedPitchNumber(row.pitchOfGame);
    },
    [setSelectedPitchNumber]
  );

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
      onRowSelect={handleRowSelect}
      isRowSelected={isRowSelected}
    />
  );
};

export default UmpirePitchCallChart;
